// usePaneWidth.ts — the user-resizable width of the Sessions sidebar.
//
// Persisted to localStorage so it survives reloads. The divider between
// the two panes is draggable; we deliberately do NOT auto-expand or snap —
// once the user picks a width, it stays put. Clamping and parsing live in
// turnState.ts.
import React, { useCallback, useEffect, useState } from 'react';
import { clampPaneWidth, parsePaneWidth } from './turnState';

const PANE_WIDTH_KEY = 'thirdeye.sessions.leftPaneWidth';

export interface PaneWidth {
  width: number;
  /** onMouseDown for the divider: starts a drag. */
  onDividerDown: (e: React.MouseEvent<HTMLDivElement>) => void;
}

export function usePaneWidth(): PaneWidth {
  const [width, setWidth] = useState<number>(() => {
    try {
      return parsePaneWidth(localStorage.getItem(PANE_WIDTH_KEY));
    } catch { /* localStorage may be unavailable in some embeds */ }
    return parsePaneWidth(null);
  });
  useEffect(() => {
    try { localStorage.setItem(PANE_WIDTH_KEY, String(width)); }
    catch { /* ignore */ }
  }, [width]);

  const onDividerDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = width;
    // Disable text selection during the drag so the cursor stays crisp.
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    const onMove = (ev: MouseEvent) => {
      setWidth(clampPaneWidth(startW, ev.clientX - startX));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = prevUserSelect;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [width]);

  return { width, onDividerDown };
}
